<script lang="ts">
	import Loader from "@components/Loader.svelte";
	import CartOrderSummary from "@components/cart/CartOrderSummary.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { initSentry } from "@utils/initSentry.js";
	import { type Snippet, onMount } from "svelte";
	import arrowLeftIcon from "@core/assets/icons/arrow.svg?raw";
	import { CategoryType } from "@core/schema/CategoryType.js";
	import type { Country } from "@core/schema/Country.js";
	import { getCategoryPath } from "@core/schema/paths/getCategoryPath.js";
	import { getCoursesListingPath } from "@core/schema/paths/getCoursesListingPath.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { CartStep, getCartStepPath, getPreviousStep } from "../../cart/CartStep.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./CartLayout.translations.json" with { type: "json" };
	import Layout from "./Layout.svelte";

	const translate = getTranslate(translations);

	const backToStepLabelMap: Record<Exclude<CartStep, CartStep.ReadyToSend>, string> = {
		[CartStep.PaymentDelivery]: translate("backToCart"),
		[CartStep.Address]: translate("backToPaymentDelivery"),
	};

	const globalSettings = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();
	const {
		currentStep,
		children,
		selectedCountry,
	}: { currentStep: Exclude<CartStep, CartStep.ReadyToSend>; children: Snippet; selectedCountry?: Country } = $props();

	const href = $derived.by(() => {
		try {
			return getCartStepPath(getPreviousStep(currentStep), tenant);
		} catch {
			return Cart.state?.hasCourseOrder
				? getCoursesListingPath(tenant)
				: getCategoryPath(tenant.locale, CategoryType.Coffee);
		}
	});

	onMount(async () => {
		await initSentry(globalSettings);
	});
</script>

<Layout pageHasMobileBottomBar>
	{#if Cart.loaded}
		<div class="my-10 mb-8">
			<Link {href} class="inline-flex items-center gap-4 text-fcrgray hover:text-black">
				<Icon icon={arrowLeftIcon} class="size-4.5 text-black rotate-180" />
				<div class="font-medium text-4.5">
					<Formatted text={backToStepLabelMap[currentStep]} />
				</div>
			</Link>
		</div>
		<div class="grid grid-cols-5 gap-10 md:gap-x-5 xl:gap-x-29 mb-10">
			{@render children()}
			<div class="col-span-full lg:col-span-2 mt-8 sm:mt-0">
				<CartOrderSummary {currentStep} cartCountry={selectedCountry ?? Cart.state?.order.deliveryAddress?.country} />
			</div>
		</div>
	{:else}
		<Loader />
	{/if}
</Layout>
